import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, RawButton, Text } from '@common-fe/common-fe';

export type BadAddressBannerVariant = 'mail' | 'provider';

interface Props {
  isMobile: boolean;
  onReviewClick: () => void;
  variant?: BadAddressBannerVariant;
  withoutReviewButton?: boolean;
  checksCount?: number;
}

const BadAddressBanner: React.FC<Props> = ({
  isMobile,
  onReviewClick,
  variant = 'mail',
  withoutReviewButton,
  checksCount = 0,
}) => {
  const { t } = useTranslation();

  const isProvider = variant === 'provider';

  const title = isProvider ? t('Check delivery issue:') : t('Mail delivery issue:');

  const message = useMemo(() => {
    if (isProvider) {
      return `${checksCount} ${t("item couldn’t be delivered. Please review the provider’s address to continue.")}`;
    }
    if (withoutReviewButton) {
      return t('Mail sent to your address was returned. Please work with your administrator to correct your address.');
    }
    return t('Mails sent to your address were returned. Please review your address to make sure it is correct.');
  }, [isProvider, checksCount, withoutReviewButton, t]);

  const reviewLabel = isProvider ? t('Review') : t('Review Address');
  const showReviewButton = isProvider || !withoutReviewButton;

  return (
    <Box
      width="100%"
      background="warningBorder"
      justify="center"
      align="center"
      pad={isMobile
        ? { top: 'spacing16', bottom: 'spacing8', horizontal: 'spacing12' }
        : { vertical: 'spacing24', horizontal: 'spacing32' }}
      direction="row"
      gap="spacing8"
      style={{
        position: 'sticky',
        zIndex: 1003,
        top: 0,
      }}
      data-testid={isProvider ? 'provider-check-banner' : 'bad-address-banner'}
    >
      {isMobile ? (
        <Text size="large" color="textBody">
          <Text size="large" weight="bold" color="textBody">
            {title}
          </Text>
          {' '}
          {message}
          {showReviewButton && ' '}
          {showReviewButton && (
            <RawButton
              data-testid="bad-address-banner_review-link"
              onClick={onReviewClick}
            >
              <Text size="large" weight="bold" color="textAccent">
                {reviewLabel}
              </Text>
            </RawButton>
          )}
        </Text>
      ) : (
        <>
          <Box direction="row" align="center" gap="spacing8" wrap>
            <Text size="large" weight="bold" color="textBody">
              {title}
            </Text>
            <Text size="large" color="textBody">
              {message}
            </Text>
          </Box>
          {showReviewButton && (
            <RawButton
              data-testid="bad-address-banner_review-link"
              onClick={onReviewClick}
              margin={{ left: 'spacing8' }}
            >
              <Text size="large" color="textAccent" weight="bold">
                {reviewLabel}
              </Text>
            </RawButton>
          )}
        </>
      )}
    </Box>
  );
};

export default BadAddressBanner;
